import { Capacitor } from '@capacitor/core';
import { NativePurchases, PURCHASE_TYPE } from '@capgo/native-purchases';

// Plugin: @capgo/native-purchases (PBL v8 compatible)
// Docs: https://capgo.app/docs/plugins/native-purchases/

type BillingInterval = 'month' | 'year';

export type BillingPlanKey = 'familiar';

// Product IDs configuration (matching Google Play Console setup)
const ANDROID_PRODUCT_IDS: Record<BillingPlanKey, Record<BillingInterval, string>> = {
  familiar: {
    month: 'familiar_monthly',
    year: 'familiar_yearly',
  },
};

// Base plan identifiers for Google Play Subscriptions (PBL v8 requires base plan ID)
const ANDROID_PLAN_IDS: Record<BillingPlanKey, Record<BillingInterval, string>> = {
  familiar: {
    month: 'monthly-plan', // Replace with your actual Base Plan ID from Google Play Console if different
    year: 'yearly-plan',   // Replace with your actual Base Plan ID from Google Play Console if different
  },
};

export function isGooglePlayBillingSupported(): boolean {
  return Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform();
}

export function getKnownAndroidPurchase(plan: BillingPlanKey, productIdentifier: string): { interval: BillingInterval } | null {
  const byInterval = ANDROID_PRODUCT_IDS[plan];
  const entries = Object.entries(byInterval) as Array<[BillingInterval, string]>;
  const found = entries.find(([, id]) => id === productIdentifier);
  if (!found) return null;
  return { interval: found[0] };
}

export function getAndroidProductId(plan: BillingPlanKey, interval: BillingInterval): string {
  return ANDROID_PRODUCT_IDS[plan][interval];
}

export function getAndroidPlanId(plan: BillingPlanKey, interval: BillingInterval): string {
  return ANDROID_PLAN_IDS[plan][interval];
}

export async function getProductDetailsAndroid(plan: BillingPlanKey, interval: BillingInterval): Promise<any> {
  if (!isGooglePlayBillingSupported()) {
    throw new Error('Google Play Billing not supported on this platform');
  }

  const productIdentifier = getAndroidProductId(plan, interval);
  
  // Capgo uses getProducts() with an array of product IDs and productType
  const result = await NativePurchases.getProducts({
    productIdentifiers: [productIdentifier],
    productType: PURCHASE_TYPE.SUBS,
  });

  const product = result?.products?.find((p: any) => p.productIdentifier === productIdentifier);
  if (!product) {
    throw new Error(`Google Play: No se encontraron detalles para el producto (${productIdentifier})`);
  }

  return {
    responseCode: 0,
    data: product,
  };
}

export async function purchaseAndroidSubscription(plan: BillingPlanKey, interval: BillingInterval): Promise<void> {
  if (!isGooglePlayBillingSupported()) {
    throw new Error('Google Play Billing not supported on this platform');
  }

  const productIdentifier = getAndroidProductId(plan, interval);
  const planIdentifier = getAndroidPlanId(plan, interval);

  try {
    // Validate availability first
    const productsRes = await NativePurchases.getProducts({
      productIdentifiers: [productIdentifier],
      productType: PURCHASE_TYPE.SUBS,
    });
    
    const exists = productsRes?.products?.some((p: any) => p.productIdentifier === productIdentifier);
    if (!exists) {
      throw new Error(`Google Play: El producto no está disponible (${productIdentifier})`);
    }

    // Purchase via NativePurchases (PBL v8)
    const transaction = await NativePurchases.purchaseProduct({
      productIdentifier,
      productType: PURCHASE_TYPE.SUBS,
      planIdentifier,
    });

    // eslint-disable-next-line no-console
    console.log('Google Play purchase success transaction:', transaction);
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error('Google Play purchaseProduct failed', { productIdentifier, e });
    const msg = String(e?.message || e?.error || e || 'unknown').trim();
    throw new Error(msg || 'Google Play Billing error');
  }
}

// We mock the purchase listener to avoid breaking code that registers it.
// Capgo's purchaseProduct resolves directly on success, which makes listeners optional or unnecessary.
// We preserve this function signature so that consumer files do not throw errors.
export async function addAndroidPurchaseListener(handler: (payload: any) => void): Promise<() => void> {
  // eslint-disable-next-line no-console
  console.log('addAndroidPurchaseListener registered with Capgo billing module.');
  return () => {};
}
